
import { Input, Output, EventEmitter, Directive } from '@angular/core';

import { isNullOrUndefined } from '@app/util/util';
import { CalendarField } from '@app/modules/shared/base-search-bar/entities/calendar.entities';

import { Airport } from '@app/modules/core/interfaces/airport';
import { FlightType } from '@search-bar/entities/flight-type';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SearchType } from '@search-bar/entities/search-type';
import { ChangeInputPayload } from '@app/modules/shared/base-search-bar/entities/search-input-payload';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';

@Directive()
export abstract class CalendarInputsAbstract {

  @Input() translate: number = 0;
  @Input() returnPlaceholder: string;
  @Input() departurePlaceholder: string;
  @Input() activeType: CalendarField;
  @Input() minValue: string;
  @Input() maxValue: string;
  @Input() isMobile: boolean;
  @Input() isExchangeCalendar: boolean = false;

  @Input() searchType: SearchType;
  @Input() flightType: FlightType;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() autoCompletePlaces: AutocompletePlaces;
  @Input() airports: Airport[];
  @Input() departurePlace: AutocompletePlace;
  @Input() departureDate: Date;
  @Input() arrivalPlace: AutocompletePlace;
  @Input() arrivalDate: Date;
  @Input() activeFlightClass: FlightClass;
  @Input() defaultPlace: SelectAutocompletePlace;
  @Input() isLoadingAutocompletePlaces: boolean;

  @Output() active: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();

  @Output() emitArrangement: EventEmitter<PassengerArrangement> = new EventEmitter<PassengerArrangement>();
  @Output() selectFlightCalendar: EventEmitter<Date[]> = new EventEmitter<Date[]>();
  @Output() emitFlightClassSelection: EventEmitter<FlightClass> = new EventEmitter<FlightClass>();
  @Output() placeInputChanged: EventEmitter<ChangeInputPayload> = new EventEmitter<ChangeInputPayload>();
  @Output() selectPlace: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() selectFlightType: EventEmitter<FlightType> = new EventEmitter<FlightType>();
  @Output() emitSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() swapLocationsEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() removeAutoCompletePlaces: EventEmitter<void> = new EventEmitter<void>();

  public showCalendarInput: boolean = false;
  public activeCalendarType: CalendarField = CalendarField.departure;
  public departure: PlaceType = PlaceType.DEPARTURE;
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public flightTypes: FlightType[] = [
    FlightType.ROUNDTRIP,
    FlightType.ONEWAY
  ];
  public isSubmitted: boolean = false;

  get activeFlightClassText(): FlightClass {
    return isNullOrUndefined(this.activeFlightClass) ? FlightClass.CHEAPER : this.activeFlightClass;
  }

  get showCalendarFlightInputs(): boolean {
    if (this.isExchangeCalendar) return false;
    else {
      return this.isMobile ? this.searchType === SearchType.HOME_FLIGHT || this.searchType === SearchType.FLIGHT_RESULTS ||
      this.searchType === SearchType.HOME_BUSES || this.searchType === SearchType.BUSES_RESULTS :
        this.searchType === SearchType.HOME_FLIGHT || this.searchType === SearchType.HOME_BUSES;
    }
  }

  get isResultsPage(): boolean {
    return (!this.isMobile && this.searchType === SearchType.FLIGHT_RESULTS) || this.searchType === SearchType.BUSES_RESULTS ||
      this.searchType === SearchType.PACKAGES_RESULT;
  }

  get isBuses(): boolean {
    return this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES;
  }

  get isEmptyArrivalPlace(): boolean {
    return this.arrivalPlace && isNullOrUndefined(this.arrivalPlace.code) && this.isSubmitted;
  }

  get isEmptyDepartureDate(): boolean {
    return isNullOrUndefined(this.departureDate) && this.isSubmitted;
  }

  get isEmptyArrivalDate(): boolean {
    return isNullOrUndefined(this.arrivalDate) && this.isSubmitted;
  }

  public selectCalendarInput(type: CalendarField): void {
    this.showCalendarInput = true;
    this.activeCalendarType = type;
  }

  public hideCalendarInput(): void {
    this.showCalendarInput = false;
  }

  public applyCalendar(event: Date[]): void {
    this.selectFlightCalendar.emit(event);
    this.hideCalendarInput();
  }

  public isActiveFlightType(type: string): boolean {
    return type === this.flightType;
  }

  public swapLocations(): void {

    this.swapLocationsEmitter.emit();
  }

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }

  get arrivalText(): string {
    return !isNullOrUndefined(this.maxValue) ? this.returnPlaceholder : 'DATE';
  }

  get departureText(): string {
    return !isNullOrUndefined(this.minValue) ? this.departurePlaceholder : 'DATE';
  }

  public isActive(type: CalendarField): boolean {
    return this.activeType === type;
  }

}
